import { useEffect, useState } from "react";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import WhyFast from "./components/WhyFast";
import CaseStudies from "./components/CaseStudies";
import PricingCards from "./components/PricingCards";
import Problem from "./components/Problem";
import UseCases from "./components/UseCases";
import Packages from "./components/Packages";
import SupportSection from "./components/SupportSection";
import FAQ from "./components/FAQ";
import ContactForm from "./components/ContactForm";
import Footer from "./components/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "株式会社ピーアールアイ",
  "alternateName": "PRI AI Sprint",
  "url": siteUrl,
  "logo": "https://aisprint.jp/assets/img/logo_header_ja.svg",
  "description": "生成AIを活用したPoC開発により企業のDX推進を加速します。「動くアプリ」を最短1週間でお届けし、貴社のビジネスを前進させます。",
  "sameAs": ["https://pr-i.co.jp/"]
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "PoC開発支援 | 株式会社ピーアールアイ",
  "url": siteUrl,
  "description": "株式会社ピーアールアイは、生成AIを活用したPoC開発により企業のDX推進を加速します。動くアプリを最短1週間でお届けします。",
  "publisher": {
    "@type": "Organization",
    "name": "株式会社ピーアールアイ"
  }
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "PoC開発支援サービス",
  "provider": {
    "@type": "Organization",
    "name": "株式会社ピーアールアイ"
  },
  "description": "AIを活用した高速PoC開発。1〜2週間で動くプロトタイプを提供。",
  "areaServed": "JP",
  "serviceType": "PoC開発・プロトタイプ開発"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "本当に1〜2週間で動くものができるのですか?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "はい。AI活用開発では、人間とAIが分担して開発を進めるため、従来の3〜5倍の速度で開発が進みます。ただし、スコープを適切に絞ることが重要です。初日に要件を確定し、優先順位をつけて確実に完了させます。"
      }
    },
    {
      "@type": "Question",
      "name": "品質は大丈夫ですか?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "毎日のレビューで品質を担保しています。AIが生成したコードは必ず人間がレビューし、テストも実施します。また、受入基準を事前に合意することで、期待値のずれを防ぎます。"
      }
    },
    {
      "@type": "Question",
      "name": "セキュリティ面は問題ないですか?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "情報システム部門のご要件に応じて柔軟に対応します。環境分離(開発/本番)、データ匿名化、アクセスログ管理、NDA締結など、必要なセキュリティ対策を講じます。クラウド/閉域網の選択も可能です。"
      }
    },
    {
      "@type": "Question",
      "name": "途中で仕様を変更できますか?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "期間内であれば、追加費用なしで方向修正が可能です。毎日のレビューでフィードバックをいただき、翌日には反映します。大幅なスコープ変更の場合は、次期間での対応となります。"
      }
    },
    {
      "@type": "Question",
      "name": "契約や支払いの流れは?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "まずは無料相談(15分)で要件をヒアリングし、お見積書をお出しします。発注確定後、着手金50%をお支払いいただき、開発開始。成果物納品後に残金50%をお支払いいただきます。NDAや契約書は弊社でひな形をご用意しております。"
      }
    }
  ]
};

export default function Home() {
  const [showFloating, setShowFloating] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowFloating(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen font-sans">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <main>
        <Hero />
        <CaseStudies />
        <WhyFast />
        <PricingCards />
        <SupportSection />
        <Problem />
        <UseCases />
        <Packages />
        <FAQ />
        <ContactForm />
      </main>
      <Footer />

      {/* Floating CTA */}
      <a
        href="#contact"
        className={`fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-full shadow-lg transition-all duration-300 text-sm whitespace-nowrap cursor-pointer ${
          showFloating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        aria-label="無料モックを依頼"
      >
        無料モックを依頼
      </a>
    </div>
  );
}